import { useState } from "react";
import { toast } from "sonner";
import { Award, BadgeCheck, Ban, CalendarPlus, CheckCircle2, Loader2, PauseCircle, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorStudents, useVendorCertificates, useVendorFormations, useVendorRefresh } from "@/hooks/useVendor";
import { formatDate } from "@/lib/format";
import { AdminCard, Pill, Td, Th, TableWrap, inputCls } from "@/pages/admin/ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS = {
  pending: { label: "En attente de paiement", tone: "amber" },
  active: { label: "Actif", tone: "green" },
  completed: { label: "Terminé", tone: "green" },
  suspended: { label: "Suspendu", tone: "slate" },
  expired: { label: "Expiré", tone: "rose" },
  revoked: { label: "Révoqué", tone: "rose" },
};

export default function VendorStudents() {
  const [q, setQ] = useState("");
  const [formationFilter, setFormationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { data: students, isLoading } = useVendorStudents({ q, formation_id: formationFilter, status: statusFilter });
  const { data: certificates } = useVendorCertificates();
  const { data: formations } = useVendorFormations();
  const refresh = useVendorRefresh();
  const [sending, setSending] = useState("");

  const act = async (row, action, days = 0) => {
    try {
      await api.put(`/vendor/formation-access/${row.id}`, { action, days });
      toast.success("Accès mis à jour");
      refresh(["vendor-students", "vendor-badges"]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendCertificate = async (row) => {
    if (!window.confirm(`Envoyer le certificat « ${row.formation_title} » à ${row.customer_name || row.customer_email} ?`)) return;
    setSending(row.id);
    try {
      const { data } = await api.post(`/vendor/formation-access/${row.id}/certificate`);
      toast.success(data.created ? "Certificat envoyé — l'étudiant a été notifié 🎓" : "Certificat déjà délivré");
      refresh(["vendor-students", "vendor-certificates"]);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(""); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-students">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">Étudiants & accès</h1>
        <p className="mt-1 text-sm text-slate-500">Gérez les étudiants de vos formations, leur progression et leurs certificats.</p>
      </div>

      <Tabs defaultValue="students">
        <TabsList className="bg-transparent p-0">
          <TabsTrigger value="students" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Étudiants & accès</TabsTrigger>
          <TabsTrigger value="certificates" data-testid="vendor-tab-certificates" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-white">Certificats</TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-5">
          <AdminCard title="Mes étudiants" description="L'accès est créé automatiquement après confirmation du paiement. Ajustez-le manuellement si besoin.">
            <div className="mb-3 flex flex-wrap gap-2">
              <div className="relative max-w-xs flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un étudiant..." data-testid="vstudents-search" />
              </div>
              <select className={`${inputCls} w-52`} value={formationFilter} onChange={(e) => setFormationFilter(e.target.value)} data-testid="vstudents-formation-filter">
                <option value="">Toutes mes formations</option>
                {(formations || []).map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
              </select>
              <select className={`${inputCls} w-44`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Tous les statuts</option>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>

            {isLoading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
            ) : (
              <TableWrap>
                <table className="w-full text-left">
                  <thead><tr className="border-b border-line">
                    <Th>Étudiant</Th><Th>Formation</Th><Th>Progression</Th><Th>Statut</Th><Th>Certificat</Th><Th className="text-right">Actions</Th>
                  </tr></thead>
                  <tbody>
                    {(students || []).map((s) => {
                      const st = STATUS[s.status] || STATUS.active;
                      return (
                        <tr key={s.id} className="border-b border-line/70" data-testid={`vstudent-row-${s.id}`}>
                          <Td>
                            <span className="font-bold text-ink">{s.customer_name || "—"}</span>
                            <span className="block text-xs text-slate-400">{s.customer_email}</span>
                          </Td>
                          <Td><span className="text-slate-600">{s.formation_title}</span></Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-brand" style={{ width: `${s.progress}%` }} /></div>
                              <span className="text-xs font-bold text-ink">{s.progress}%</span>
                            </div>
                          </Td>
                          <Td><Pill tone={st.tone}>{st.label}</Pill></Td>
                          <Td>
                            {s.has_certificate ? (
                              <span className="flex items-center gap-1 text-xs font-bold text-amber-600"><Award className="h-3.5 w-3.5" /> Certifié</span>
                            ) : (
                              <button onClick={() => sendCertificate(s)} disabled={!!sending} data-testid={`send-cert-${s.id}`}
                                className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-amber-600 disabled:opacity-60">
                                {sending === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Award className="h-3 w-3" />} Envoyer
                              </button>
                            )}
                          </Td>
                          <Td className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {s.status !== "active" && s.status !== "completed" && (
                                <button onClick={() => act(s, "grant")} title="Accorder / Activer" data-testid={`vgrant-${s.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"><CheckCircle2 className="h-4 w-4" /></button>
                              )}
                              <button onClick={() => act(s, "extend", 30)} title="Prolonger (+30j)" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-brand-soft hover:text-brand"><CalendarPlus className="h-4 w-4" /></button>
                              <button onClick={() => act(s, "suspend")} title="Suspendre" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"><PauseCircle className="h-4 w-4" /></button>
                              <button onClick={() => act(s, "revoke")} title="Révoquer" data-testid={`vrevoke-${s.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500"><Ban className="h-4 w-4" /></button>
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!(students || []).length && <p className="py-12 text-center text-sm text-slate-500">Aucun étudiant pour l'instant.</p>}
              </TableWrap>
            )}
          </AdminCard>
        </TabsContent>

        <TabsContent value="certificates" className="mt-5">
          <AdminCard title="Certificats délivrés" description="Certificats de vos formations envoyés aux étudiants.">
            <TableWrap>
              <table className="w-full text-left">
                <thead><tr className="border-b border-line"><Th>N° Certificat</Th><Th>Étudiant</Th><Th>Formation</Th><Th>Délivré le</Th></tr></thead>
                <tbody>
                  {(certificates || []).map((c) => (
                    <tr key={c.id} className="border-b border-line/70" data-testid={`vcert-row-${c.id}`}>
                      <Td><span className="flex items-center gap-1.5 font-mono text-xs font-bold text-brand"><BadgeCheck className="h-4 w-4" /> {c.certificate_number}</span></Td>
                      <Td>{c.user_name || "—"}</Td>
                      <Td><span className="text-slate-600">{c.formation_title}</span></Td>
                      <Td>{formatDate(c.issued_at)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!(certificates || []).length && <p className="py-12 text-center text-sm text-slate-500">Aucun certificat délivré.</p>}
            </TableWrap>
          </AdminCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
